import * as React from 'react';
import {
  FormControl, InputLabel, Input, InputProps, InputAdornment, Button,
  Menu, MenuItem, FormHelperText, makeStyles, TextField,
} from '@material-ui/core';
import {
  Country, countries, formatNumber
} from './phone-data';


interface PhoneFieldProps extends InputProps {
  label?: string;
  helperText?: string;
  className?: string;
  defaultCountryCode?: string;
  onCountryCodeChange?: (c: number) => void;
  newStyle?: boolean;
}

const useStyles = makeStyles(() => ({
  flagButton: {
    minWidth: 65,
    padding: 0,
    height: 30,
  },
  native: {
    width: 30,
    height: 30,
    padding: 8,
  },
  nativeRoot: {
    padding: 0,
    '& + svg': {
      display: 'none',
    },
  },
  nativeSelect: {
    padding: 0,
    lineHeight: 0,
    height: 11,
  },
  positionStart: {
    position: 'relative',
  },
  countryName: {
    marginRight: '6px'
  },
  dialCode: {
    color: '#6b6b6b'
  },
  arrow: {
    position: 'relative',
    top: '50%',
    marginTop: '-1px',
    left: '20px',
    width: 0,
    height: 0,
    borderLeft: '3px solid transparent',
    borderRight: '3px solid transparent',
    borderTop: '4px solid #555'
  },
  flag: {
    width: '16px',
    height: '11px',
    backgroundImage: 'url("/assets/flags.png") !important',
    // get more css here: https://github.com/alexplumb/material-ui-phone-number/blob/master/src/styles.less
    '&.au': {
      backgroundPosition: '-192px 0'
    },
    '&.ca': {
      backgroundPosition: '-16px -22px'
    },
    '&.gb': {
      backgroundPosition: '-80px -44px'
    },
    '&.us': {
      backgroundPosition: '-112px -143px'
    },
    '&.mx': {
      backgroundPosition: '-240px -88px'
    },
    '&.aw': {
      backgroundPosition: '-208px 0'
    }
  }
}));

const countriesWithLengths = countries.map(c => {
  const length = c.format.replace(/[^\.]/g, '').length + String(c.dialCode).length;
  return {
    ...c,
    length
  };
});

const guessCountryCode = (text = ''): Country => {
  const numbers = text.replace(/\D/g, '');
  const prefixMatch = text.match(/^\+d+/);
  if (!prefixMatch) {
    return null;
  }
  const possibleCountries = countriesWithLengths.filter(({ length }) => length === numbers.length).sort((a, b) => a.priority - b.priority);
  const selectedCountry = possibleCountries.find(c => {
    const start = numbers.indexOf(c.dialCode);
    return start === 0;
  });

  return selectedCountry;
};

export const PhoneField = ({
  label = '',
  helperText = '',
  className = '',
  defaultCountryCode = 'us',
  onChange,
  value,
  onCountryCodeChange,
  newStyle,
  ...props }: PhoneFieldProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const [selectedCode, setSelectedCode] = React.useState<string>(defaultCountryCode);
  const [text, setText] = React.useState<string>((value || '') as string);
  const selectedCountry = React.useMemo<Country>(() => countries.find(({ iso2 }) => iso2 === selectedCode), [selectedCode]);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [focus, setFocus] = React.useState(false);
  const [selected, setSelected] = React.useState(false);

  React.useEffect(() => {
    const inputNumber = text.replace(/\D/g, '');
    const findCode = guessCountryCode(text);
    if (findCode) {
      setText(formatNumber(inputNumber.slice(findCode.dialCode.length), findCode));
      setSelectedCode(findCode.iso2);
    } else {
      setText(formatNumber(inputNumber, selectedCountry));
    }
  }, [selectedCountry]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };


  const handleSelect = (code: string) => () => {
    const newSelectedCountry = countries.find(({ iso2 }) => iso2 === code);
    const inputNumber = text.replace(/\D/g, '');
    setText(formatNumber(inputNumber, newSelectedCountry));
    setSelectedCode(code);
    setAnchorEl(null);
    setSelected(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const prefix = '';

    let formattedNumber = prefix;

    if (onChange) e.persist();

    // Does exceed default 15 digit phone number limit
    if (value.replace(/\D/g, '').length > 15) {
      return;
    }

    // ie hack
    e.preventDefault();


    if (value.length > 0) {
      const inputNumber = value.replace(/\D/g, '');

      formattedNumber = formatNumber(inputNumber, selectedCountry);
    }

    let caretPosition = e.target.selectionStart;
    const oldFormattedText = text;
    const diff = formattedNumber.length - oldFormattedText.length;

    if (diff === 0) {
      return;
    }

    setText(formattedNumber);
    if (onChange) {
      e.target.value = '+' + selectedCountry.dialCode + ' ' + formattedNumber;
      onChange(e);
    }

    const totalFormat = '+' + selectedCountry.dialCode + ' ' + selectedCountry.format;
    const properLength = totalFormat.length;
    onCountryCodeChange(properLength);

    setTimeout(() => {
      if (diff > 0) {
        caretPosition = caretPosition - diff;
      }
      const lastChar = formattedNumber.charAt(formattedNumber.length - 1);

      if (lastChar === ')') {
        inputRef.current.setSelectionRange(formattedNumber.length - 1, formattedNumber.length - 1);
      } else if (caretPosition > 0 && oldFormattedText.length >= formattedNumber.length) {
        inputRef.current.setSelectionRange(caretPosition, caretPosition);
      }
    });

  };

  if (newStyle) {
    return (
      <TextField
        fullWidth
        id={props.id}
        label={label}
        error={props.error}
        helperText={helperText}
        onFocus={(prop) => {
          props.onFocus?.(prop);
          setFocus(true);
        }}
        onBlur={(prop) => {
          props.onBlur?.(prop);
          setTimeout(() => {
            setFocus(false);
          }, 200);
        }}
        onChange={(e) => handleInput(e as React.ChangeEvent<HTMLInputElement>)}
        value={text}
        variant='outlined'
        size='small'
        style={{ width: '100%' }}
        type="tel"
        InputProps={{
          style: { borderRadius: 10 },
          startAdornment: (focus || Boolean(anchorEl) || text || selected) ? (
            <InputAdornment position="start">
              <Button
                className={classes.flagButton}
                aria-owns={anchorEl ? 'country-menu' : null}
                aria-label="Select country"
                onClick={handleClick}
                aria-haspopup
              >
                <div className={classes.flag + ' ' + selectedCountry?.iso2} style={{ marginLeft: '-7px' }}>
                  <div className={classes.arrow}></div>
                </div>
                <span style={{ marginLeft: '15px', color: 'gray' }}>+{selectedCountry.dialCode}</span>
              </Button>
              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {countries.map(({ name, iso2, dialCode }) => (
                  <MenuItem key={iso2} className="country" onClick={handleSelect(iso2)}>
                    <div className={`${classes.flag} ${iso2}`} style={{ marginRight: '8px' }} />
                    <span className={classes.countryName}>
                      {name}
                    </span>
                    <span className={classes.dialCode}>{`+${dialCode}`}</span>
                  </MenuItem>
                ))}
              </Menu>
            </InputAdornment>
          ) : null
        }}
      />
    );
  }

  return (<FormControl className={className} style={{ width: '100%' }}>
    <InputLabel htmlFor={props.id}>{label}</InputLabel>
    <Input
      id={props.id}
      inputRef={inputRef}
      onChange={handleInput}
      value={text}
      type="tel"
      startAdornment={
        <InputAdornment position="start">
          <Button
            className={classes.flagButton}
            aria-owns={anchorEl ? 'country-menu' : null}
            aria-label="Select country"
            onClick={handleClick}
            aria-haspopup
          >
            <div className={classes.flag + ' ' + selectedCountry?.iso2} style={{ marginLeft: '-7px' }}>
              <div className={classes.arrow}></div>
            </div>
            <span style={{ marginLeft: '15px', color: 'gray' }}>+{selectedCountry.dialCode}</span>
          </Button>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {countries.map(({ name, iso2, dialCode }) => (
              <MenuItem key={iso2} className="country" onClick={handleSelect(iso2)}>
                <div className={`${classes.flag} ${iso2}`} style={{ marginRight: '8px' }} />
                <span className={classes.countryName}>
                  {name}
                </span>
                <span className={classes.dialCode}>{`+${dialCode}`}</span>
              </MenuItem>
            ))}
          </Menu>
        </InputAdornment>
      }
      {...props}
    />
    <FormHelperText error={props.error}>{helperText}</FormHelperText>
  </FormControl>);
};
